import {
    Badge,
    Button,
    Card,
    CardBody,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    SimpleGrid,
    Text,
    useColorModeValue,
    VStack
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { FiCalendar, FiFilter, FiSearch, FiX } from 'react-icons/fi';
import { AccessFilters as FilterType } from '../hooks/useAccessList';

interface AccessFiltersProps {
  filters: FilterType;
  onFilter: (filters: FilterType) => void;
  onClearFilters: () => void;
  totalRecords: number;
  currentRecords: number;
  isLoading: boolean;
}

const storeOptions = [
  { value: '', label: 'Todas as lojas' },
  { value: 'loja-principal', label: 'Loja Principal' },
  { value: 'loja-secundaria', label: 'Loja Secundária' },
  { value: 'italia-consulado', label: 'Itália Consulado' },
];

const AccessFilters: React.FC<AccessFiltersProps> = ({
  filters,
  onFilter,
  onClearFilters,
  totalRecords,
  currentRecords,
  isLoading
}) => {
  const [localFilters, setLocalFilters] = useState<FilterType>(filters);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleInputChange = (field: keyof FilterType, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const handleSearch = () => {
    onFilter(localFilters);
  };

  const handleClear = () => {
    setLocalFilters({});
    onClearFilters();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const activeFiltersCount = Object.values(localFilters).filter(Boolean).length;
  const hasActiveFilters = activeFiltersCount > 0;

  return (
    <Card>
      <CardBody>
        <VStack spacing={4}>
          {/* Contador de Registros */}
          <HStack w="100%" justify="space-between" align="center">
            <HStack spacing={2}>
              <Text fontSize="lg" fontWeight="bold" color="gray.700">
                Acessos Realizados
              </Text>
              {hasActiveFilters && (
                <Badge colorScheme="blue" variant="solid">
                  {activeFiltersCount} filtro{activeFiltersCount > 1 ? 's' : ''}
                </Badge>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.600">
              Mostrando <strong>{currentRecords.toLocaleString()}</strong> de{' '}
              <strong>{totalRecords.toLocaleString()}</strong> registros
            </Text>
          </HStack>

          {/* Filtros */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} w="100%">
            {/* Busca por Nome */}
            <InputGroup>
              <InputLeftElement>
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                ref={searchInputRef}
                placeholder="Buscar por nome..."
                value={localFilters.userName || ''}
                onChange={(e) => handleInputChange('userName', e.target.value)}
                onKeyPress={handleKeyPress}
                bg={bgColor}
                borderColor={borderColor}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
              />
            </InputGroup>

            {/* Seletor de Loja */}
            {/* <Select
              placeholder="Selecionar loja"
              value={localFilters.store || ''}
              onChange={(e) => handleInputChange('store', e.target.value)}
              bg={bgColor}
              borderColor={borderColor}
              _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
            >
              {storeOptions.slice(1).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select> */}

            {/* Data Início */}
            <InputGroup>
              <InputLeftElement>
                <Icon as={FiCalendar} color="gray.400" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="Data início"
                value={localFilters.startDate || ''}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                bg={bgColor}
                borderColor={borderColor}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
              />
            </InputGroup>

            {/* Data Fim */}
            <InputGroup>
              <InputLeftElement>
                <Icon as={FiCalendar} color="gray.400" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="Data fim"
                value={localFilters.endDate || ''}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                bg={bgColor}
                borderColor={borderColor}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
              />
            </InputGroup>
          </SimpleGrid>

          {/* Botões de Ação */}
          <HStack spacing={4} w="100%" justify="flex-end">
            <Button
              leftIcon={<FiX />}
              variant="outline"
              onClick={handleClear}
              isDisabled={!hasActiveFilters || isLoading}
              size="md"
            >
              Limpar Filtros
            </Button>
            <Button
              leftIcon={<FiFilter />}
              colorScheme="blue"
              onClick={handleSearch}
              isLoading={isLoading}
              size="md"
            >
              Filtrar
            </Button>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default AccessFilters; 